(function (root, factory) {
  const engine = factory();
  if (typeof module === 'object' && module.exports) module.exports = engine;
  else root.WSEngine = engine;
})(typeof globalThis === 'object' ? globalThis : this, function () {
  'use strict';
  const whole = n => Number.isSafeInteger(n) && n >= 0 && n <= 10000;
  const round = n => Math.round((n + Number.EPSILON) * 100) / 100;
  function unit(model, quantity) {
    if (!whole(quantity) || quantity < model.minimum) return null;
    return [...model.tiers].reverse().find(t => quantity >= t.from)?.mills ?? null;
  }
  function validLines(lines, catalog) {
    const totals = new Map(), errors = [];
    for (const line of lines) {
      const model = catalog.models.find(m => m.handle === line.model);
      if (!model || !whole(line.quantity) || line.quantity < 1) { errors.push({code:'invalid',line:line.id}); continue; }
      if (!Object.hasOwn(catalog.logo.prices, line.logo)) errors.push({code:'logo',line:line.id});
      if (!model.logo && line.logo !== 'none') errors.push({code:'footwear_logo',line:line.id});
      totals.set(model.handle, (totals.get(model.handle) || 0) + line.quantity);
    }
    for (const [handle,quantity] of totals) {
      const model = catalog.models.find(m => m.handle === handle);
      if (quantity < model.minimum) errors.push({code:'minimum',model:handle,minimum:model.minimum,quantity});
      if (quantity > 10000) errors.push({code:'maximum',model:handle});
    }
    return {errors,totals};
  }
  function price(lines, catalog) {
    const validation=validLines(lines,catalog), priced=[];
    let clothing=0,printing=0,eligible=true;
    for (const line of lines) {
      const model=catalog.models.find(m=>m.handle===line.model);
      if (!model || !whole(line.quantity) || !Object.hasOwn(catalog.logo.prices,line.logo)) continue;
      const mills=unit(model,validation.totals.get(model.handle));
      if (mills===null) {eligible=false; continue;}
      const rawClothing=mills*line.quantity;
      const logoCents=model.logo?catalog.logo.prices[line.logo]*line.quantity:0;
      clothing+=rawClothing;printing+=logoCents;
      priced.push({...line,modelRecord:model,unitMills:mills,clothingMills:rawClothing,printing:logoCents});
    }
    const netClothing=Math.round(clothing/10);
    return {lines:priced,clothing:netClothing,printing,net:netClothing+printing,gross:Math.round((netClothing+printing)*1.21),errors:validation.errors,valid:eligible&&!validation.errors.length};
  }
  function pack(lines,catalog) {
    const bins=[], groups={clothing:[],shoes:[],boots:[]};
    for (const line of lines) {
      const model=catalog.models.find(m=>m.handle===line.model);
      if (!model || !whole(line.quantity)) continue;
      const category=catalog.categories.find(c=>c.id===model.category);
      const group=category.packingGroup;
      if (!groups[group] || !Number.isFinite(category.units) || category.units<=0) throw new Error('Invalid packing rule');
      for(let i=0;i<line.quantity;i++)groups[group].push({handle:model.handle,units:category.units,grams:model.grams??null});
    }
    for (const [group,items] of Object.entries(groups)) {
      items.sort((a,b)=>b.units-a.units||(b.grams??0)-(a.grams??0));
      const groupBins=[];
      for (const item of items) {
        if(item.grams>20000) throw new Error('An item exceeds the parcel weight limit');
        let bin=groupBins.find(b=>b.units+item.units<=80&&b.grams+(item.grams??0)<=20000);
        if(!bin){bin={group,units:0,grams:0,unknownWeight:false,items:[]};groupBins.push(bin);}
        bin.units+=item.units;bin.grams+=item.grams??0;bin.unknownWeight ||= item.grams===null;bin.items.push(item.handle);
      }
      bins.push(...groupBins);
    }
    return {bins,count:bins.length,weightConfirmed:bins.every(b=>!b.unknownWeight),volumeEstimate:true};
  }
  function shipping(lines,catalog,country,orderNet) {
    const packing=pack(lines,catalog);
    if (!packing.count) return {...packing,minimum:0,maximum:0,free:false,estimated:false};
    const tariff=catalog.shipping[country];
    if(!tariff)return {...packing,minimum:null,maximum:null,free:false,estimated:true,unsupported:true};
    if(tariff.freeFrom!==undefined&&orderNet>=tariff.freeFrom)return {...packing,minimum:0,maximum:0,free:true,estimated:false};
    if(tariff.flat!==undefined)return {...packing,minimum:packing.count*tariff.flat,maximum:packing.count*tariff.flat,free:false,estimated:false};
    let minimum=0,maximum=0;
    for(const bin of packing.bins){
      const bracket=grams=>grams<=2000?0:grams<=5000?1:grams<=10000?2:3;
      minimum+=tariff.rates[bracket(bin.grams)];
      maximum+=tariff.rates[bin.unknownWeight?3:bracket(bin.grams)];
    }
    return {...packing,minimum,maximum,free:false,estimated:!packing.weightConfirmed};
  }
  function colourMatch(model,colour) {
    const patterns={black:/black|zwart/i,grey:/grey|gray|grijs|anthracite|graphite/i,white:/white|wit|off.white/i,navy:/navy|marine|ink/i};
    return model?.variantsVerified&&patterns[colour]?model.colours.find(c=>patterns[colour].test(c))||null:null;
  }
  function recommendation(answers,catalog) {
    const heavy=['construction','heavy'].includes(answers.work);
    const level=answers.level==='auto'?(heavy?'premium':answers.work==='service'?'comfort':'basic'):answers.level;
    const category=answers.category||'shirts';
    const eligible=catalog.models.filter(m=>m.category===category);
    const pattern=['black','grey','white','navy'].includes(answers.colour);
    const match=m=>colourMatch(m,answers.colour);
    let preferred=eligible.find(m=>m.level===level)||eligible[0];
    if(answers.level==='auto'&&pattern&&!match(preferred)){
      const matching=eligible.filter(m=>match(m));
      preferred=matching.find(m=>m.level===level)||matching.find(m=>m.level==='premium'&&heavy)||matching[0]||preferred;
    }
    const matchedColour=match(preferred)||null;
    const colourStatus=!pattern?'any':matchedColour?'match':preferred.variantsVerified?'unavailable':'unverified';
    return {model:preferred,level:preferred.level,reason:heavy?'heavy':'general',protectionRequired:answers.protection==='yes',availabilityVerified:preferred.variantsVerified===true,matchedColour,colourStatus};
  }
  function logoResolution(width,height,position,catalog) {
    const bounds=catalog.logo.bounds[position];
    if(!bounds||!Number.isFinite(width)||!Number.isFinite(height)||width<=0||height<=0)return null;
    const ratio=Math.min(bounds.width/width,bounds.height/height);
    const widthCm=round(width*ratio),heightCm=round(height*ratio);
    const exactDpi=2.54/ratio;
    const dpi=Math.round(exactDpi);
    return {widthCm,heightCm,dpi,acceptable:exactDpi>=catalog.logo.minimumDpi,preferred:exactDpi>=300};
  }
  function kit(people,items,catalog) {
    if (!whole(people)||people<1||people>1000) return [];
    return items.filter(i=>whole(i.perPerson)&&i.perPerson>0).map(i=>{
      const model=catalog.models.find(m=>m.handle===i.model);
      if(!model)throw new Error('Unknown kit model');
      const requested=people*i.perPerson;
      return {model:model.handle,quantity:Math.max(model.minimum,requested),requested,adjusted:requested<model.minimum,logo:model.logo?(i.logo||'none'):'none'};
    });
  }
  return Object.freeze({whole,unit,validLines,price,pack,shipping,recommendation,colourMatch,logoResolution,kit});
});
